import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useChatStore } from '../../store/chatStore';
import {
  LayoutGrid,
  MessageCircle,
  Mail,
  // 2026-09-08 — 주소록(`/contacts`) fallback 아이콘.
  Contact,
  // 2026-09-09 — AI(`/ai`) fallback 아이콘(설계 판 A).
  Sparkles,
  Calendar,
  CalendarDays,
  Target,
  Folder,
  BookOpen,
  Network,
  FileText,
  Clock,
  FileCheck,
  // 2026-08-26 — 인사 메뉴(`/erp/hr`) fallback 아이콘. 레지스트리가 custom:* 을 주면 그쪽이 이긴다.
  UserCog,
  Settings,
  type LucideIcon, BarChart3,
  // 2026-09-13 — 급여 복원(ICON_BY_NAME 의 Wallet).
  Wallet,
  // 2026-09-13 — 도움말(`/help`) fallback · 레지스트리 icon='HelpCircle'.
  HelpCircle,
  // 2026-09-07 — 레일 넘침 화살표(H1-5).
  ChevronUp, ChevronDown,
} from '../../icons';
import { Badge } from '../shared/Badge';
import { Avatar } from '../shared/Avatar';
import { useAuthStore } from '../../store/authStore';
import { avatarImageUrl } from '../../lib/avatar-src';
import {
  tLayout, tLayoutRail, getLayoutLang, subscribeLayoutLang,
  type LayoutI18nKey,
} from '../../lib/layout-i18n';
// 2026-08-11 — 세로 배치(아이콘 위/라벨 아래) 도입에 따른 최하단 "전체메뉴" 항목 + 패널.
import { useAllMenuStore } from '../../store/allMenuStore';
// 조직도 항목 클릭 → 내부 팝업(라우팅 대체). 항목 자체는 레지스트리/ fallback 그대로.
import { anchorOf, useOrgDirectoryStore } from '../../store/orgDirectoryStore';
import { AllMenuPanel } from './AllMenuPanel';
// 2026-08-17 (사용자 지시) — "전체메뉴를 클릭하면 프로필처럼 팝업" → 드로어 대신 앵커 팝오버.
//   종전 드로어(AllMenuPanel)는 롤백 경로로 그대로 두고 렌더 한 줄에서만 갈린다.
import { AllMenuPopover } from './AllMenuPopover';
import { ALLMENU_POPOVER_ENABLED, RAIL_OVERFLOW_ENABLED, RAIL_ARROWS_ENABLED } from '../../lib/layout-shell';
// 2026-08-26 — 레일 넘침 접기. 재는 것과 접는 것은 훅에, 그리는 것은 아래 «더보기» 칸·팝오버에.
import { foldRail, isRailItemCurrent, useRailCapacity } from './useRailOverflow';
import { RailMoreItem, RailMorePopover } from './RailMorePopover';
// 2026-09-07 — 레일 머리(토글 + 사용자 줄) · 넘침 화살표 · 라벨 칩. 정본 H1-3 · H1-4 · H1-5.
import { RailHead } from './RailHead';
import { useRailScroll, scrollActiveIntoView } from './useRailScroll';
import { RailLabelChip, type RailChipTarget } from './RailLabelChip';
// 전체메뉴 아이콘 — 레지스트리가 나르는 레일 아이콘들과 같은 두톤 세트(icons:icon-74).
//   이 버튼만 레지스트리 메뉴가 아니라 프론트 버튼이라 여기서 그린다.
import { DuoAllMenu } from '../../icons/duotone';
// 별(★) 고정 목록 — 자리가 모자랄 때 «누가 남느냐» 에만 쓴다(순서는 레지스트리가 SSOT).
import { useFavoritesStore } from '../../store/favoritesStore';
// 접힘/펼침 — 머리의 토글(H1-4)과 Ctrl/Cmd+B 가 같은 store 를 본다.
import { useSidebarStore } from '../../store/sidebarStore';
// 메뉴 레지스트리(/menu-config) 공유 스토어 — 상태바(⑥)와 같은 스냅샷을 본다(요청 1회).
import { useMenuConfig, type MenuConfigRow } from '../../lib/menu-config';
// 메뉴 보이기 판정 — 레일·전체메뉴·즐겨찾기가 **한 판정**을 쓴다(2026-09-01).
import { canSeeMenuPath, useIsRepresentativeViewer } from '../../lib/menu-visibility';
// 2026-08-12 — 브랜드(회사 로고) 블록. 관리 콘솔 "회사정보 관리"가 SSOT.
import './NavBar.css';

// 내 프로필 진입점 경로 — NavBarItem 이 이 경로의 항목은 아이콘 대신 사용자 아바타로 렌더.
// 정적(navToRender)·동적(dynToRender, /menu-config) 양경로 공통 판정 (design-20260606 §16-I1).
const PROFILE_PATH = '/me';

// 2026-08-18 — 즐겨찾기(⑤ 레일 최하단)가 "좌측 레일 메뉴의 바로가기" 라서 같은 목록이 필요하다.
//   목록 SSOT 를 복제하지 않도록 타입·fallback 배열·아이콘 화이트리스트만 export 한다
//   (`lib/menu-catalog.ts` 가 소비). 렌더 경로·값은 한 줄도 바뀌지 않는다.
export interface NavItem {
  to: string;
  labelKey: LayoutI18nKey;
  icon: LucideIcon;
  badge?: number;
}

/**
 * 인사 셸의 뿌리 경로 (2026-08-26) — 레일 항목과 라우터가 같은 값을 본다.
 *
 * 이 메뉴는 **전 직원**이 본다. 같은 주소가 보는 사람에 따라 갈라지기 때문이다 —
 * 회사 관리자는 «인사 현황», 직원은 «내 인사»(오늘 출퇴근 · 내 연차 · 내 신청).
 * 담당자 화면(직원 명부·연차·권한…)은 라우트마다 RoleGuard 가 막는다.
 */
export const HR_PATH = '/erp/hr';

// 급여(payroll)·인사(hr) menu 는 사용자 결정으로 **의도적 비노출**.
//   ⚠️ 복원 금지: V2.6.1 제거 → V2.7.197~200 가 "PRD ORG/PAY/HR/APR 봉합" 명목으로 자동 복원했던 이력 있음.
//   PRD(init-docs)에 PAY-001~007 / HR-001~015 항목이 있어도 본 메뉴는 다시 추가하지 않는다.
//   (i18n 키 layout.nav.payroll/hr, 백엔드 라우트/모델, 페이지 컴포넌트는 보존 — 메뉴/라우트만 미노출.)
// ★ 조직도는 2026-07-24, **전자결재(approval)는 2026-08-24 결정 ① 로 열렸다** — 위 금지는 그 둘에
//   대해서는 더 이상 유효하지 않다. 근태의 휴가·연장·정정·외근·출장이 전부 결재를 타기 때문이다.
//   전자결재 메뉴의 실노출 SSOT 는 admin_menu_registry(menu_key='approval') 이고 아래 fallback 에도 넣는다.
// ★★ 인사는 2026-08-26 「인사 메뉴 반영」로 **새 경로 `/erp/hr` 에서 열렸다**(아래 fallback 참조).
//   위 금지가 가리키는 것은 옛 경로 `/hr`(HRPage) 이고 그것은 지금도 닫혀 있다 — 근태가 `/hr` 이
//   아니라 `/erp/attendance` 로 열린 것과 **같은 규칙**이다.
// ★★★ 2026-09-13 — **사용자 결정으로 급여(`/payroll`)도 열린다**(관제 경유).
//   사용자 원문: 「급여 메뉴도 복원해줘」(2026-09-13 · 관제가 전달, 리뷰가 관제에 원문 확인).
//   원문을 적는 이유 — 이 금지가 경계한 것은 «권한을 확인할 수 없는 복원» 이었다. 주석만 보고도
//   권한 사슬을 되짚을 수 있어야 한다. 위 「복원 금지」 기록은
//   «왜 닫혀 있었나» 의 이력으로 남기고 지우지 않는다 — 다만 **그 금지는 이제 유효하지 않다**.
//   ⚠ 위 경고가 경계한 것은 「PRD 에 PAY 항목이 있다」는 이유로 **AI 가 스스로 되살리는 것**이었다
//     (V2.7.197~200 이 실제로 그랬다). 이번은 그것이 아니라 **사용자가 명시적으로 지시한 복원**이다.
//     같은 구분이 조직도(2026-07-24)·전자결재(2026-08-24)·인사(2026-08-26)에도 적용됐다.
//   이제 이 주석에서 닫힌 채로 남는 것은 **옛 인사 경로 `/hr`(HRPage) 하나뿐**이다.
//   급여 메뉴의 실노출 SSOT 도 admin_menu_registry(menu_key='payroll') 이고 아래 fallback 에도 넣는다.
// /board route 는 BoardsPage (게시판, migration 102). /info(BoardPage)는 메뉴에서 빠졌지만 라우트는 남는다.
// Cycle 70 — labelKey 타입 안전 LayoutI18nKey (tLayout(labelKey) 매핑, ko/en/ja 3 lang).
export const PRIMARY_NAV: NavItem[] = [
  { to: '/dashboard', labelKey: 'layout.nav.dashboard', icon: LayoutGrid },
  { to: '/chat', labelKey: 'layout.nav.chat', icon: MessageCircle, badge: 0 },
  { to: '/mail', labelKey: 'layout.nav.mail', icon: Mail, badge: 0 },
  // 2026-09-08 — 주소록(migration 171). **실노출 SSOT 는 admin_menu_registry**
  //   (menu_key='contacts', url='/contacts') 이고 본 항목은 조회 실패 시 fallback 이다 —
  //   ai-minutes·reservation 이 fallback 에 빠져 조회 실패 경로에서만 메뉴가 사라졌던
  //   결함과 같은 클래스를 처음부터 피한다.
  { to: '/contacts', labelKey: 'layout.nav.contacts', icon: Contact },
  // 2026-09-09 — AI (설계 판 A). **실노출 SSOT 는 admin_menu_registry**(menu_key='ai', url='/ai')
  //   이고 본 항목은 조회 실패 시 fallback 이다 — ai-minutes·reservation 이 fallback 에 빠져
  //   조회 실패 경로에서만 메뉴가 사라졌던 결함과 같은 클래스를 처음부터 피한다.
  //   ⚠ 레지스트리 행이 들어가기 전까지 **기존 회사의 운영에서는 보이지 않는다**(새 회사는 씨앗으로 받는다).
  { to: '/ai', labelKey: 'layout.nav.ai', icon: Sparkles },
  { to: '/calendar', labelKey: 'layout.nav.calendar', icon: Calendar },
  // 2026-07-25 — **프로젝트 메뉴 = 3c 프로젝트 홈(/project)**(레이아웃 SSOT 05.프로젝트 3c).
  //   2026-07-24 에 /task 직결로 바꿨던 것을 되돌린다 — 3c 홈에서 위젯·좌측 네비로 고른 뒤
  //   3d(/task)로 내려가는 흐름이 시안이다. /task 는 별칭으로 같은 메뉴에 묶인다(아래 ACTIVE_ALIAS_PREFIX).
  //   ⚠ 실노출 SSOT 는 admin_menu_registry(menu_key='task', label='프로젝트') — 본 항목은 fallback 이며
  //     레지스트리 url 도 /project 로 되돌려야 실제 메뉴가 바뀐다.
  { to: '/project', labelKey: 'layout.nav.project', icon: Target },
  { to: '/workspace', labelKey: 'layout.nav.workspace', icon: Folder },
  // 2026-08-18 — 알림사항(/info) → 게시판(/board). 실노출 SSOT 는 admin_menu_registry
  //   (menu_key='board') 이고 본 항목은 조회 실패 시 fallback 이다.
  { to: '/board', labelKey: 'layout.nav.board', icon: BookOpen },
  // 2026-08-07 — AI 회의록. 레지스트리(admin_menu_registry menu_key='ai-minutes', enabled=true,
  //   icon='custom:aidoc')에는 이미 있었으나 본 fallback 배열에 빠져 있어, /menu-config 조회가
  //   실패하는 경로에서만 메뉴가 사라졌다. 실노출 SSOT 는 여전히 레지스트리 — 여기는 fallback 정합용.
  //   (fallback 은 lucide 화이트리스트만 쓰므로 custom:aidoc 대신 FileText 로 근사.)
  { to: '/ai-minutes', labelKey: 'layout.nav.ai_minutes', icon: FileText },
  // 2026-08-12 — 예약. 레지스트리(menu_key='reservation', icon='custom:reservation_new')에 이미
  //   있으나 fallback 배열에 빠지면 /menu-config 조회 실패 경로에서 메뉴가 사라진다(ai-minutes 와
  //   같은 결함 클래스). fallback 은 lucide 화이트리스트만 쓰므로 CalendarDays 로 근사.
  { to: '/reservation', labelKey: 'layout.nav.reservation', icon: CalendarDays },
  // 2026-08-23 — 근태(근태 1단계). **실노출 SSOT 는 admin_menu_registry**(menu_key='attendance',
  //   url='/erp/attendance') 이고 본 항목은 레지스트리 조회 실패 시 fallback 이다 —
  //   ai-minutes·reservation 이 fallback 에 빠져 조회 실패 경로에서만 메뉴가 사라졌던 결함과
  //   같은 클래스를 처음부터 피한다. 레지스트리 행이 들어가기 전까지 운영에서는 보이지 않는다.
  { to: '/erp/attendance', labelKey: 'layout.nav.attendance', icon: Clock },
  // 2026-08-24 — 전자결재(결정 ①). 레지스트리 조회 실패 시 fallback.
  { to: '/approval', labelKey: 'layout.nav.approval', icon: FileCheck },
  // 2026-08-26 — 인사. **실노출 SSOT 는 admin_menu_registry**(menu_key='hr', url='/erp/hr') 이고
  //   본 항목은 조회 실패 시 fallback 이다(근태·전자결재와 같은 규칙). 레지스트리 행이 들어가기
  //   전까지 운영에서는 보이지 않는다. i18n 키(layout.nav.hr)는 처음부터 있던 것을 그대로 쓴다.
  { to: HR_PATH, labelKey: 'layout.nav.hr', icon: UserCog },
  // 2026-09-13 — 급여(사용자 결정 복원). **실노출 SSOT 는 admin_menu_registry**(menu_key='payroll',
  //   url='/payroll') 이고 본 항목은 조회 실패 시 fallback 이다(근태·전자결재·인사와 같은 규칙).
  //   레지스트리 행이 들어가기 전까지 운영에서는 보이지 않는다 — 씨앗은 빈 표에만 심기 때문이다
  //   (admin/backend menu-registry.ts `ensureMenuTable`). i18n 키(layout.nav.payroll)는 비노출
  //   기간에도 보존돼 있던 것을 그대로 쓴다.
  { to: '/payroll', labelKey: 'layout.nav.payroll', icon: Wallet },
  // 2026-09-16 — 업무보고. **실노출 SSOT 는 admin_menu_registry**(menu_key='briefing',
  //   url='/briefing', sort 97) 이고 본 항목은 조회 실패 시 fallback 이다.
  //   ⚠ 이 줄이 없던 동안 무엇이 깨졌나: 씨앗에는 처음부터 있었는데(2026-09-04 판 P8) 이 배열에만
  //     빠져 있어, /menu-config 조회가 실패하는 경로에서 **관리자·대표에게도 업무보고가 사라졌다.**
  //     ai-minutes·reservation 이 겪은 것과 같은 결함 클래스다(위 주석들).
  //   ★ 권한은 이 배열이 아니라 `canSeeMenuPath` 가 가린다 — 레지스트리 가지·폴백 가지 **양쪽에**
  //     똑같이 걸리므로(아래 primaryAll.filter), 여기 넣는다고 직원에게 보이지 않는다.
  //     폴백에서 빼는 것은 「숨김」이 아니라 「조회 실패 때 볼 사람도 못 봄」이었다.
  //   ☑ 이 어긋남은 `lib/menu-fallback-registry.sync.test.ts` 가 앞으로 막는다.
  { to: '/briefing', labelKey: 'layout.nav.briefing', icon: BarChart3 },
];

// 하단 영역 (2026-07-24, 사용자 요청) — 내 프로필(아바타)·세션 유지 시계 제거 → 조직도·통합설정.
//   · 내 프로필 진입점은 우측 사이드 레일의 프로필 아바타가 담당(세션 시계도 그 아래로 이동).
//   · 조직도는 위 PRIMARY_NAV 주석의 '의도적 비노출' 대상이었으나 **2026-07-24 사용자 지시로 노출**한다
//     (급여/인사/전자결재는 계속 비노출 — 그 셋은 복원 금지 유지).
//   ⚠ 실제 노출 SSOT 는 admin_menu_registry(DB) — 본 배열은 조회 실패 시 fallback 이다.
/** 조직도 경로 — 이 항목만 라우팅 대신 내부 팝업을 연다(2026-08-17). 레지스트리 url 과 같은 값. */
const ORG_DIRECTORY_PATH = '/organization';

export const SECONDARY_NAV: NavItem[] = [
  { to: ORG_DIRECTORY_PATH, labelKey: 'layout.nav.organization', icon: Network },
  // 2026-09-13 — 도움말. **실노출 SSOT 는 admin_menu_registry**(menu_key='help', url='/help', 105) 이고
  //   본 항목은 조회 실패 시 fallback 이다. 조직도(101)와 통합설정(110) 사이 — 모든 사용자에게 뜬다.
  { to: '/help', labelKey: 'layout.nav.help', icon: HelpCircle },
  { to: '/settings', labelKey: 'layout.nav.settings_integrated', icon: Settings },
];

// ── 동적 메뉴(관리자 사이트 메뉴 레지스트리 연동) ─────────────────────────────
// 8444 admin 이 관리하는 메뉴/노출 상태를 /menu-config(edge→admin-backend)에서 읽어
// 하드코딩 메뉴를 대체한다. 실패/빈 응답 시 위 PRIMARY_NAV/SECONDARY_NAV 로 fallback(회귀 방지).
export const ICON_BY_NAME: Record<string, LucideIcon> = {
  LayoutGrid, MessageCircle, Mail, Calendar, Target, Folder, BookOpen, Settings,
  // 2026-08-26 — 인사 fallback 아이콘.
  UserCog,
  // 2026-07-24 — 조직도(레지스트리 icon='Network') 아이콘 해석용 additive 등록.
  Network,
  // 2026-08-07 — AI 회의록 fallback 아이콘. 레지스트리 기본값은 custom:aidoc(SVG)이지만
  //   admin 이 lucide 이름 'FileText' 로 바꿔도 해석되도록 화이트리스트에 추가.
  FileText,
  // 2026-08-12 — 예약 fallback 아이콘(레지스트리 기본값은 custom:reservation_new SVG).
  CalendarDays,
  // 2026-08-23 — 근태(레지스트리 icon='Clock'). **화이트리스트에 없으면 LayoutGrid 로 떨어져**
  //   대시보드와 같은 아이콘이 두 개 뜬다(위 toDyn 의 `?? LayoutGrid`).
  Clock,
  // 2026-08-24 — 전자결재(레지스트리 icon='FileCheck').
  FileCheck,
  // 2026-09-01 — 업무보고(레지스트리 icon='BarChart3'). 화이트리스트에 없으면 LayoutGrid 로
  //   떨어져 **대시보드와 같은 아이콘이 둘** 뜬다(위 근태 주석과 같은 함정).
  BarChart3,
  // 2026-09-08 — 주소록(레지스트리 icon='Contact'). 화이트리스트에 없으면 LayoutGrid 로
  //   떨어져 **대시보드와 같은 아이콘이 둘** 뜬다(근태·업무보고가 겪은 그 함정).
  Contact,
  // 2026-09-09 — AI(레지스트리 icon='Sparkles'). 위와 같은 함정을 처음부터 피한다.
  Sparkles,
  // 2026-09-13 — 급여(레지스트리 icon='Wallet'). 사용자 결정으로 급여가 복원되며 추가.
  //   ★ 왜 Wallet 인가: 이 화이트리스트에 **급여를 뜻하는 이름이 하나도 없었다**(Clock=근태 ·
  //     UserCog=인사 · FileCheck=전자결재 · BarChart3=업무보고 — 아무거나 쓰면 뜻이 겹친다).
  //     `PayrollPage` 가 제 화면에서 이미 쓰는 아이콘이 Wallet 이라(PayrollPage.tsx:3·129)
  //     레일과 화면이 같은 기호를 쓰게 된다. 근태·업무보고·주소록·AI 가 저마다 제 아이콘을
  //     여기에 **추가**해 온 것과 같은 처리다(위 주석들).
  Wallet,
  // 2026-09-13 — 도움말(레지스트리 icon='HelpCircle'). 없으면 LayoutGrid 로 떨어진다(위와 같은 함정).
  HelpCircle,
};
interface DynItem { to: string; label: string; icon: LucideIcon; svg?: string; section: string; external: boolean; menuKey: string; }

type MenuCfg = { primary: DynItem[]; secondary: DynItem[]; aux: DynItem[] };

/**
 * 보조영역(2026-08-19 사용자 지시) — 관리 콘솔 "메뉴 관리 > 위치 = 보조영역".
 * 이 자리의 메뉴는 **화면(좌측 레일·하단 상태바)에는 뜨지 않고 전체메뉴에만** 보인다.
 * 사용자는 전체메뉴에서 별(★)을 눌러 자기 우측 레일에 직접 고정한다.
 */
export const AUX_SECTION = 'aux';

function rowsToCfg(menus: MenuConfigRow[]): MenuCfg {
  const toDyn = (m: MenuConfigRow): DynItem => ({
    to: m.url, label: m.label, icon: ICON_BY_NAME[m.icon] ?? LayoutGrid,
    svg: m.svg, // 커스텀 아이콘(custom:*) 은 SVG 본문을 직접 렌더
    section: m.section, external: !!m.is_external, menuKey: m.menu_key,
  });
  // 2026-08-11 — section='status' 는 **하단 상태바 전용** 자리다(StatusBar 가 그린다).
  //   여기서 제외하지 않으면 같은 메뉴가 좌측 레일에도 중복으로 뜬다.
  // 2026-08-19 — section='aux'(보조영역)도 같은 이유로 레일에서 뺀다. 다만 상태바와 달리
  //   **전체메뉴에는 나와야** 하므로 버리지 않고 별도 묶음으로 넘긴다(아래 aux).
  return {
    primary: menus
      .filter((m) => m.section !== 'secondary' && m.section !== 'status' && m.section !== AUX_SECTION)
      .map(toDyn),
    secondary: menus.filter((m) => m.section === 'secondary').map(toDyn),
    aux: menus.filter((m) => m.section === AUX_SECTION).map(toDyn),
  };
}

// v2.11.1 — 최초 페인트 플래시 봉합: 마지막 성공 응답(원본 rows)을 localStorage 에 캐시해
// 첫 렌더부터 활성 메뉴만 표시. 캐시 없는 최초 방문은 스켈레톤 → 응답 도착 시 실 메뉴.
// 하드코딩 fallback 은 "조회 실패 + 캐시 없음" 일 때만 (기존 회귀 방지 의미 유지).
//
// 2026-08-11 — 캐시/조회 로직 자체는 `lib/menu-config.ts` 싱글턴으로 이관했다(의미 동일).
//   상태바(⑥)도 같은 레지스트리를 봐야 해서 소비자가 둘이 되었고, 각자 fetch 하면 목록이
//   갈라지는 순간이 생기기 때문. NavBar 는 그 스냅샷을 rowsToCfg 로 해석만 한다.

/** 좌측 바 '통합설정' 경로 — 관리자에게만 노출하고 하단 목록의 **맨 마지막**에 둔다(2026-08-11). */
const INTEGRATED_SETTINGS_PATH = '/settings';


export function NavBar() {
  useSyncExternalStore(subscribeLayoutLang, getLayoutLang, getLayoutLang);
  // 채팅 미읽음 배지 — 전체 room unreadCount 합계 (TopBar 가 전역 동기화). 0 이면 NavBarItem 이 미표시.
  const chatUnread = useChatStore((s) => s.chatUnreadTotal);
  const { rows, pending } = useMenuConfig();
  const cfg = useMemo<MenuCfg | null>(() => (rows ? rowsToCfg(rows) : null), [rows]);
  // 통합설정 노출 판정용 권한 — 관리자(ADMIN/SYSTEM_ADMIN)만. UX 게이트이며 권위 있는 차단은
  // 백엔드가 담당한다(RoleGuard 주석의 경계 선언과 같은 원칙).
  const role = useAuthStore((s) => s.user?.role);
  const isAdmin = role === 'ADMIN' || role === 'SYSTEM_ADMIN';
  // 「업무보고」는 관리자 **또는 대표** — 대표는 역할이 STAFF 일 수 있다(2026-09-01).
  const isRep = useIsRepresentativeViewer();
  // 전체메뉴 팝오버의 앵커 — 팝오버는 body 로 portal 되므로 버튼 위치를 실측해서 붙인다.
  //   (레일은 overflow:hidden 이라 안에 두면 잘린다 — ProfilePopover 와 같은 사정)
  const allMenuAnchorRef = useRef<HTMLButtonElement | null>(null);
  /**
   * 2026-08-26 — 레일 넘침 접기. 레일(nav)과 주 목록(ul)을 실측해 «몇 칸을 그릴 수 있나» 를 얻는다.
   * 붙박이(브랜드·전체메뉴·하단 목록)는 주 목록에 몇 개가 들어 있든 높이가 그대로라 되먹임이 없다.
   */
  const navRef = useRef<HTMLElement | null>(null);
  const primaryListRef = useRef<HTMLUListElement | null>(null);
  const moreAnchorRef = useRef<HTMLButtonElement | null>(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const { pathname } = useLocation();
  /**
   * 고정한 메뉴는 접힘 후보 중 마지막까지 남는다 (2026-08-26). 저장소는 우측 레일 즐겨찾기와
   * **같은 것**을 쓴다 — 담는 것이 이미 «좌측 레일 메뉴를 가리키는 포인터» 라 새로 만들 것이 없다.
   * `hydrate` 는 같은 사용자면 no-op 이라, 전체메뉴 팝오버가 이미 불렀어도 서로를 덮지 않는다
   * (여기서도 부르는 이유 = 팝오버 플래그가 꺼져도 우선순위는 살아 있어야 한다).
   */
  const favoriteItems = useFavoritesStore((s) => s.items);
  const hydrateFavorites = useFavoritesStore((s) => s.hydrate);
  const favUserId = useAuthStore((s) => s.user?.id ?? null);
  useEffect(() => { hydrateFavorites(favUserId); }, [favUserId, hydrateFavorites]);
  const pinnedPaths = useMemo(() => new Set(favoriteItems.map((f) => f.to)), [favoriteItems]);

  // 동적 설정(응답 또는 캐시)이 있으면 그것으로, 없으면 하드코딩 fallback.
  // pending(캐시 없는 최초 로드) 동안은 fallback 전체를 그리지 않고 스켈레톤 —
  // "전체 메뉴 → 활성 메뉴" 플래시 봉합 (v2.11.1).
  const primaryAll: RenderItem[] = cfg
    ? cfg.primary.map((d) => dynToRender(d, d.menuKey === 'chat' ? chatUnread : undefined))
    : PRIMARY_NAV.map((i) => navToRender(i, i.to === '/chat' ? chatUnread : undefined));
  // 2026-08-26 — 인사는 **전 직원에게** 그린다. 직원은 그 메뉴에서 «내 인사»(오늘 출퇴근 ·
  //   내 연차 · 내 신청)를 보고, 담당자 화면은 라우트마다 RoleGuard 가 막는다.
  /**
   * ★★ 「업무보고」(`/briefing`)만은 **관리자 또는 대표**에게만 그린다 (2026-09-01).
   *
   *    통합설정처럼 관리자 전용으로 잠그면 **대표가 못 본다** — 대표는 역할이 `STAFF` 일 수
   *    있다. 판정은 `menu-visibility` 의 `canSeeMenuPath` 하나를 쓴다(레일·전체메뉴·즐겨찾기가
   *    갈리면 「메뉴는 있는데 열면 403」이 된다).
   */
  const primary: RenderItem[] = primaryAll.filter((i) => canSeeMenuPath(i.to, isAdmin, isRep));
  const secondaryAll: RenderItem[] = cfg
    ? cfg.secondary.map((d) => dynToRender(d))
    : SECONDARY_NAV.map((i) => navToRender(i));
  // 보조영역 — 레일에는 안 그리고 전체메뉴에만 넘긴다(하드코딩 fallback 에는 이 자리가 없다).
  const aux: RenderItem[] = cfg ? cfg.aux.map((d) => dynToRender(d)) : [];

  // 2026-08-11 (사용자 지시) — 통합설정은 **관리자만** 보고, 하단 목록의 **제일 마지막**에 온다.
  //   판정은 경로(/settings) 기준이다: 레지스트리의 menu_key='settings' 행은 배포에 따라
  //   url 이 '/me'(내 프로필)로 마이그레이션된 이력이 있어(menu-registry.ts), 키로 잠그면
  //   내 프로필 진입점까지 관리자 전용이 되어 버린다.
  const isIntegratedSettings = (i: RenderItem) => i.to === INTEGRATED_SETTINGS_PATH;
  const secondary: RenderItem[] = [
    ...secondaryAll.filter((i) => !isIntegratedSettings(i)),
    ...(isAdmin ? secondaryAll.filter(isIntegratedSettings) : []),
  ];

  /**
   * 2026-08-26 — 넘치면 접는다. 사다리(`--rail-box-h`)로 먼저 압축하고, 그래도 안 들어가는 만큼만
   * «더보기» 로 넘긴다. `pending`(스켈레톤) 동안은 재지 않는다 — 그릴 목록이 아직 없다.
   * 플래그가 꺼져 있으면 capacity 가 `null` 이라 `foldRail` 이 원본을 그대로 돌려준다(회귀 0).
   */
  // 화살표(H1-5)를 쓰면 «더보기» 접기는 재지 않는다 — 둘 다 켜면 접고 또 스크롤하게 된다.
  const railCapacity = useRailCapacity(
    navRef, primaryListRef, RAIL_OVERFLOW_ENABLED && !RAIL_ARROWS_ENABLED && !pending,
  );
  const collapsed = useSidebarStore((st) => st.collapsed);
  const rail = useRailScroll(primaryListRef, RAIL_ARROWS_ENABLED && !pending);
  const [chip, setChip] = useState<RailChipTarget | null>(null);

  /**
   * 지금 보고 있는 메뉴가 **창 밖일 때만** 맞춘다 — 늘 가운데로 끌면 맨 위에서 시작해야
   * 할 때도 목록이 내려가 있어 위쪽 메뉴가 없어진 것처럼 보인다(H1-5).
   */
  useEffect(() => {
    if (!RAIL_ARROWS_ENABLED || pending) return;
    scrollActiveIntoView(primaryListRef.current, '.navbar-item-active');
  }, [pathname, pending, collapsed]);

  /** 레일 항목 위의 포인터·포커스 → 라벨 칩. 항목마다 붙이지 않고 여기서 한 번 받는다. */
  const onRailOver = useCallback((e: React.SyntheticEvent) => {
    const el = (e.target as HTMLElement | null)?.closest?.('[data-rail-label]') as HTMLElement | null;
    if (!el) return;
    const badge = Number(el.getAttribute('data-rail-badge') ?? '');
    setChip({ el, label: el.getAttribute('data-rail-label') ?? '', badge: Number.isFinite(badge) ? badge : undefined });
  }, []);
  const onRailOut = useCallback(() => setChip(null), []);

  const isCurrentItem = useCallback(
    (i: RenderItem) => isRailItemCurrent(pathname, i.to, ACTIVE_ALIAS_PREFIX),
    [pathname],
  );
  const isPinnedItem = useCallback((i: RenderItem) => pinnedPaths.has(i.to), [pinnedPaths]);
  const { visible: primaryVisible, folded: primaryFolded } = useMemo(
    () => foldRail(primary, railCapacity, { isCurrent: isCurrentItem, isPinned: isPinnedItem }),
    [primary, railCapacity, isCurrentItem, isPinnedItem],
  );

  return (
    <nav
      className="navbar"
      aria-label="Primary"
      ref={navRef}
      onMouseOver={onRailOver}
      onMouseOut={onRailOut}
      onFocus={onRailOver}
      onBlur={onRailOut}
    >
      {/* 머리 — 브랜드 + (토글 + 사용자 줄). 정본 H1-4.
          브랜드 자체는 종전 `NavBarBrand` 그대로다(로고 폴백·클릭 이동 무회귀) —
          머리가 그것을 «감쌀» 뿐이다. */}
      <RailHead />
      {pending ? (
        <ul className="navbar-list" aria-hidden="true">
          {[0, 1, 2, 3, 4].map((i) => (
            <li key={i} className="navbar-item-skeleton" style={{ animationDelay: `${i * 60}ms` }}>
              <span className="navbar-item-skeleton-icon" />
              <span className="navbar-item-skeleton-label" />
            </li>
          ))}
        </ul>
      ) : (
        <>
        {/* 넘침 화살표(H1-5) — 넘칠 때만 자리를 잡는다. 끝에 닿으면 «안 보이되 자리는 남는다»
            (걷어내면 24px 이 사라져 목록이 통째로 흔들린다 — CSS `.is-end` 참조). */}
        {rail.overflowing && (
          <button
            type="button"
            className={`rail-arrow${rail.atTop ? ' is-end' : ''}`}
            aria-label={tLayout('layout.rail.scroll_up')}
            onClick={() => rail.step(-1)}
            onPointerDown={() => rail.hold(-1)}
            onPointerUp={rail.release}
            onPointerLeave={rail.release}
          >
            <ChevronUp size={16} aria-hidden />
          </button>
        )}
        <ul className="navbar-list navbar-list-primary" ref={primaryListRef}>
          {primaryVisible.map((item) => <NavBarItem key={item.to} item={item} />)}
          {/* 넘칠 때만 생기는 칸이다 — 다 들어가는 화면에는 «더보기» 자체가 없다. */}
          {primaryFolded.length > 0 && (
            <RailMoreItem
              count={primaryFolded.length}
              open={moreOpen}
              onToggle={() => setMoreOpen((v) => !v)}
              anchorRef={moreAnchorRef}
            />
          )}
        </ul>
        {rail.overflowing && (
          <button
            type="button"
            className={`rail-arrow${rail.atBottom ? ' is-end' : ''}`}
            aria-label={tLayout('layout.rail.scroll_down')}
            onClick={() => rail.step(1)}
            onPointerDown={() => rail.hold(1)}
            onPointerUp={rail.release}
            onPointerLeave={rail.release}
          >
            <ChevronDown size={16} aria-hidden />
          </button>
        )}
        </>
      )}
      {/* 전체메뉴 — 세로 배치로 라벨이 10px 까지 줄어든 것의 짝으로, 등록된 모든 메뉴를
          넓은 카드 격자로 펼친다. 라우트가 아니라 오버레이 토글이므로 NavLink 가 아닌 button
          이다(활성 표시 대상도 아님).
          2026-08-11 (사용자 지시) — 하단 목록 안(조직도 아래)에 있던 것을 **구분선 위**로 올린다.
          하단 밀기(margin-top:auto) 책임도 이 목록이 가져간다(NavBar.css 말미 override). */}
      <ul className="navbar-list navbar-list-allmenu">
        {!pending && <AllMenuNavItem anchorRef={allMenuAnchorRef} />}
      </ul>
      {/* 하단 — 2026-07-24 사용자 요청으로 세션 유지 시계(SessionTimer) 제거.
          시계는 우측 레일을 거쳐 2026-08-11 부터 **하단 상태바 맨 오른쪽**(StatusBar)에 있고,
          만료·자동 로그아웃은 AppLayout 의 SessionIdleGuard 가 상시 담당한다(동작 무회귀).
          구분선(바인더)= `.navbar-list-bottom` 의 border-top — 그 아래가 조직도·통합설정. */}
      <ul className="navbar-list navbar-list-bottom">
        {(pending ? [] : secondary).map((item) => <NavBarItem key={item.to} item={item} />)}
      </ul>
      {/* 목록 SSOT 는 여기 하나 — 패널은 위에서 계산된 primary/secondary 를 그대로 받는다.
          (패널이 /menu-config 를 다시 읽으면 관리자 메뉴 on/off 시 두 목록이 갈라진다)
          2026-08-17 — 기본은 프로필과 같은 앵커 팝오버. `VITE_ALLMENU_POPOVER='0'` 이면
          종전 드로어(AllMenuPanel)로 되돌아간다 — 주입되는 목록은 두 경로가 동일하다. */}
      {/* 접힌 메뉴는 «더보기» 안에 있다 — 전체메뉴(모두)와 목록이 다르므로 열림 상태도 따로 둔다. */}
      {primaryFolded.length > 0 && (
        <RailMorePopover
          items={primaryFolded}
          open={moreOpen}
          onClose={() => setMoreOpen(false)}
          anchorRef={moreAnchorRef}
        />
      )}
      {/* 라벨 칩(H1-3) — 접힌 레일에서만. 펼치면 이름이 이미 보인다. */}
      <RailLabelChip target={chip} enabled={collapsed} />
      {ALLMENU_POPOVER_ENABLED ? (
        /* 보조영역(위치='aux')은 레일에 없고 여기에만 있다 — 별도 묶음으로 넘겨 그룹을 나눈다. */
        <AllMenuPopover primary={primary} secondary={secondary} aux={aux} anchorRef={allMenuAnchorRef} />
      ) : (
        /* 롤백 경로(드로어)는 그룹이 둘뿐이다 — AllMenuPanel 을 고치지 않고 주 메뉴 뒤에 잇는다. */
        <AllMenuPanel primary={aux.length > 0 ? [...primary, ...aux] : primary} secondary={secondary} />
      )}
    </nav>
  );
}

/** 최하단 "전체메뉴" 항목 — NavBarItem 과 같은 클래스를 써서 세로 배치 규칙을 그대로 탄다. */
function AllMenuNavItem({ anchorRef }: { anchorRef?: React.RefObject<HTMLButtonElement> }) {
  useSyncExternalStore(subscribeLayoutLang, getLayoutLang, getLayoutLang);
  const open = useAllMenuStore((s) => s.open);
  const toggle = useAllMenuStore((s) => s.toggle);
  const label = tLayout('layout.nav.all_menu');
  return (
    <li>
      <button
        type="button"
        ref={anchorRef}
        className={['navbar-item', 'navbar-item-allmenu', open && 'navbar-item-allmenu-open'].filter(Boolean).join(' ')}
        data-rail-label={label}
        aria-expanded={open}
        title={label}
        onClick={toggle}
      >
        <DuoAllMenu size={18} />
        <span className="navbar-label">{label}</span>
      </button>
    </li>
  );
}

// 렌더 공통 타입 — label 은 이미 해석된 문자열, external 이면 새 탭 링크. svg 있으면 커스텀 SVG 아이콘.
//
// 2026-08-11 — `label` 은 **세로 레일용(축약 가능)**, `labelFull` 은 **전체 라벨**이다.
//   레일은 폭 72px·라벨 10px 이라 ja 일부가 한 줄에 안 들어가 축약본을 쓰지만(tLayoutRail),
//   전체메뉴 패널의 카드(116px)는 좁지 않으므로 전체 라벨을 보여야 한다. 두 값을 함께 실어
//   보내 각자 맞는 것을 고르게 한다(축약 여부를 소비처가 몰라도 되게).
interface RenderItem { to: string; label: string; labelFull: string; icon: LucideIcon; svg?: string; badge?: number; external: boolean; }
function navToRender(i: NavItem, badge?: number): RenderItem {
  return { to: i.to, label: tLayoutRail(i.labelKey), labelFull: tLayout(i.labelKey), icon: i.icon, badge, external: false };
}
function dynToRender(d: DynItem, badge?: number): RenderItem {
  // 관리자 레지스트리 라벨은 관리자가 직접 입력한 문자열 → 축약하지 않는다(설정과 화면 불일치 방지).
  //   레일에서 넘치면 CSS 말줄임(…)으로 처리한다.
  return { to: d.to, label: d.label, labelFull: d.label, icon: d.icon, svg: d.svg, badge, external: d.external };
}

/**
 * 메뉴 활성 표시 별칭 — 메뉴가 가리키지 않는 경로라도 **같은 메뉴 화면**이면 활성으로 본다.
 * 2026-07-25: 프로젝트 메뉴가 3c 홈(/project)을 가리키므로, 거기서 내려가는 3d(/task)에서도
 * 같은 메뉴가 켜져 있어야 한다.
 */
export const ACTIVE_ALIAS_PREFIX: Record<string, string[]> = {
  // 프로젝트 메뉴(3c 홈)는 그 아래 화면들에서도 켜져 있어야 한다 — 3d(/task) 포함.
  '/project': ['/task'],
};

function NavBarItem({ item }: { item: RenderItem }) {
  const Icon = item.icon;
  const { pathname } = useLocation();
  // 조직도(2026-08-17) — 페이지로 넘어가는 대신 앱 위 내부 팝업을 연다.
  //   NavLink 는 그대로 둔다(주소·마크업·기존 테스트 보존). 클릭만 가로채고 라우팅을 막는다.
  //   새 탭/보조 클릭은 종전대로 /organization 페이지로 열리게 통과시킨다.
  const toggleOrgDirectory = useOrgDirectoryStore((s) => s.toggle);
  const isOrgDirectory = item.to === ORG_DIRECTORY_PATH;
  const aliasActive = (ACTIVE_ALIAS_PREFIX[item.to] ?? [])
    .some((p) => pathname === p || pathname.startsWith(`${p}/`));
  // 내 프로필 항목(/me)은 아이콘 대신 현재 사용자 아바타(이미지 없으면 이니셜)로 렌더.
  // P1 은 avatarUrl 미연동 → 이니셜. P2 에서 avatarUrl(src) 주입 (design-20260606 §17.1).
  const user = useAuthStore((s) => s.user);
  const isProfile = item.to === PROFILE_PATH;
  // 내 프로필 아바타 — avatarUrl(wcavatar/외부 URL) → 서버 스트림 URL. 비-프로필 항목은 null.
  const avatarSrc = isProfile && user ? avatarImageUrl(user.id, user.avatarUrl) : null;
  const inner = (
    <>
      {isProfile && user
        ? <span className="navbar-avataricon" aria-hidden><Avatar userId={user.id} name={user.name} src={avatarSrc} size="xs" /></span>
        : item.svg
          ? <span className="navbar-svgicon" aria-hidden dangerouslySetInnerHTML={{ __html: item.svg }} />
          : <Icon size={18} />}
      <span className="navbar-label">{item.label}</span>
      {item.badge !== undefined && item.badge > 0 && (
        <span className="navbar-badge">
          <Badge variant="count" size="sm">{item.badge > 99 ? '99+' : item.badge}</Badge>
        </span>
      )}
    </>
  );
  return (
    <li>
      {item.external ? (
        <a
          href={item.to}
          className="navbar-item"
          target="_blank"
          rel="noopener noreferrer"
          data-rail-label={item.labelFull}
          data-rail-badge={item.badge}
        >{inner}</a>
      ) : (
        <NavLink
          to={item.to}
          className={({ isActive }) => ['navbar-item', (isActive || aliasActive) && 'navbar-item-active'].filter(Boolean).join(' ')}
          data-rail-label={item.labelFull}
          data-rail-badge={item.badge}
          onClick={isOrgDirectory ? (e) => {
            // 보조 클릭(새 탭·새 창)은 건드리지 않는다 — 그 의도는 "페이지로 열기" 다.
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
            e.preventDefault();
            // 누른 항목의 화면 위치를 함께 넘긴다 — 팝업이 이 옆에 뜬다.
            toggleOrgDirectory(anchorOf(e.currentTarget));
          } : undefined}
        >
          {inner}
        </NavLink>
      )}
    </li>
  );
}
